import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

/**
 * Load image and convert to base64
 */
const loadImageAsBase64 = (imagePath) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };
    img.onerror = (error) => {
      console.error("Failed to load image:", imagePath, error);
      reject(error);
    };
    img.src = `${process.env.PUBLIC_URL}${imagePath}`;
  });
};

/**
 * Generate SCF Annexure PDF
 * Recognized Sample Collection Center/Facility
 */
export const generateSCFAnnexure = async (
  certificateData = {},
  scfData = [],
) => {
  const doc = new jsPDF({
    orientation: "portrait",
    unit: "mm",
    format: "a4",
  });

  const pageWidth = 210;
  const pageHeight = 297;

  try {
    // Use passed scfData parameter
    if (scfData && scfData.length > 0) {
      // Load images
      let headerImage, logoImage;

      try {
        headerImage = await loadImageAsBase64(
          "/certificate_images/scopeCrystalnew1.png",
        );
        console.log("✓ Header image loaded for SCF Annexure");
      } catch (err) {
        console.warn("✗ Header image not found for SCF Annexure");
      }

      try {
        logoImage = await loadImageAsBase64(
          "/certificate_images/certificate_logo.png",
        );
        console.log("✓ Logo image loaded for SCF Annexure");
      } catch (err) {
        console.warn("✗ Logo image not found for SCF Annexure");
      }

      let startY = headerImage ? 105 : 60;

      // Add title section with border
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      const titleText = "Recognised Sample Collection Center/Facility";
      const titleWidth = doc.getTextWidth(titleText);
      const titleX = (pageWidth - titleWidth) / 2;

      // Draw top border
      doc.setLineWidth(0.5);
      doc.line(5, startY - 8, pageWidth - 5, startY - 8);

      // Add title
      doc.text(titleText, titleX, startY - 3);

      // Draw bottom border
      doc.line(5, startY, pageWidth - 5, startY);

      startY += 3;

      // SCF Table columns (matching PHP: 10%, 45%, 45%)
      const tableColumn = [
        "Sno.",
        "ID of Sample Collection Center / Facility",
        "Name of Sample Collection Center / Facility,Location",
        "Address of Sample Collection Center /Facility",
      ];
      const totalPagesExp = "{total_pages_count_string}";

      const tableRows = scfData.map((scf, index) => [
        index + 1,
        scf.scfid || "",
        scf.name || "",
        scf.address || "",
      ]);

      autoTable(doc, {
        startY: startY,
        head: [tableColumn],
        body: tableRows,
        theme: "grid",
        styles: {
          fontSize: 9,
          cellPadding: 1,
          lineColor: [0, 0, 0],
          lineWidth: 0.3,
          overflow: "linebreak",
          cellWidth: "wrap",
          fillColor: false,
          textColor: [0, 0, 0],
          halign: "left",
          valign: "middle",
        },
        headStyles: {
          fillColor: false,
          textColor: [0, 0, 0],
          fontSize: 8,
          fontStyle: "bold",
          halign: "left",
          valign: "middle",
        },
        bodyStyles: {
          textColor: [0, 0, 0],
          fontSize: 9,
          fontStyle: "normal",
        },
        columnStyles: {
          0: { cellWidth: 18, halign: "center", valign: "middle" }, // Sno.
          1: { cellWidth: 52, halign: "left", valign: "middle" },   // SCF ID
          2: { cellWidth: 65, halign: "left", valign: "middle" },   // Name + Location
          3: { cellWidth: 65, halign: "left", valign: "middle" },   // Address
        },
        margin: { left: 3, right: 3, bottom: 30, top: 105 },
        didDrawPage: function (pageData) {
          const currentPage = pageData.pageNumber;


          // Background watermark logo (NABL logo in center - on every page)
          if (logoImage) {
            try {
              doc.saveGraphicsState();
              doc.setGState(new doc.GState({ opacity: 0.5 }));
              const watermarkSize = 150;
              const watermarkX = (pageWidth - watermarkSize) / 2;
              const watermarkY = (pageHeight - watermarkSize) / 2;
              doc.addImage(
                logoImage,
                "PNG",
                watermarkX,
                watermarkY,
                watermarkSize,
                watermarkSize,
              );
              doc.restoreGraphicsState();
            } catch (err) {
              console.error("Failed to add watermark:", err);
            }
          }

          // Header Image at top (repeats on every page)
          if (headerImage) {
            const headerWidth = doc.internal.pageSize.getWidth() - 20;
            const headerHeight = 38;
            const headerX = (pageWidth - headerWidth) / 2;
            doc.addImage(
              headerImage,
              "PNG",
              headerX,
              5,
              headerWidth,
              headerHeight,
            );
          }

          const headerStartY = headerImage ? 43 : 10;
          const leftMargin = 5;
          const rightMargin = 5;
          const rowHeight = 7;
          let yPos = headerStartY;

          doc.setFontSize(10.5);

          // Row 1: Laboratory Name
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(0, 0, 0);
          doc.text('Laboratory Name :', leftMargin + 2, yPos + 5);

          doc.setFont('helvetica', 'normal');

          // Full text
          
        const removeDuplicateAddressParts = (address) => {
          if (!address) return '';

          const parts = address
            .split(',')
            .map(part => part.trim());

          const cleaned = [];

          parts.forEach(part => {
            const upperPart = part.toUpperCase();

            if (
              cleaned.length === 0 ||
              cleaned[cleaned.length - 1].toUpperCase() !== upperPart
            ) {
              cleaned.push(part);
            }
          });

          return cleaned.join(', ');
        };

        const cleanedAddress = removeDuplicateAddressParts(certificateData.address);

          const labText = `${certificateData.labName}${cleanedAddress ? ', ' + cleanedAddress : ''}`;

          // Max width for wrapping
          const maxWidth = pageWidth - (leftMargin + 55) - rightMargin;

          // Split text
          const wrappedText = doc.splitTextToSize(labText, maxWidth);

          // Print wrapped text
          doc.text(wrappedText, leftMargin + 55, yPos + 5);

          // Adjust Y position based on number of lines
          yPos += wrappedText.length * 6;

          doc.setFont("helvetica", "bold");
          doc.setTextColor(0, 0, 0);
          doc.text("Accreditation Standard :", leftMargin + 2, yPos + 5);
          doc.setFont("helvetica", "normal");
          doc.text(
            certificateData.standard || "",
            leftMargin + 55,
            yPos + 5,
          );
          yPos += rowHeight;

          doc.setFont("helvetica", "bold");
          doc.setTextColor(0, 0, 0);
          doc.text("Certificate Number :", leftMargin + 2, yPos + 5);
          doc.setFont("helvetica", "normal");
          doc.text(
            certificateData.certificateNumber || "",
            leftMargin + 55,
            yPos + 5,
          );
          doc.setFont("helvetica", "bold");
          doc.setTextColor(0, 0, 0);
          doc.text("Page No :", leftMargin + 122, yPos + 5);
          doc.setFont("helvetica", "normal");
          doc.text(`${currentPage} of ${totalPagesExp}`, leftMargin + 142, yPos + 5);
          yPos += rowHeight;

          doc.setFont("helvetica", "bold");
          doc.setTextColor(0, 0, 0);
          doc.text("Validity :", leftMargin + 2, yPos + 5);
          doc.setFont("helvetica", "normal");
          doc.text(
            `${certificateData.issueDate && certificateData.validUntil ? `${certificateData.issueDate} to ${certificateData.validUntil}` : ""}`,
            leftMargin + 55,
            yPos + 5,
          );

          doc.setFont("helvetica", "bold");
          doc.setTextColor(0, 0, 0);
          doc.text("Last Amended on :", leftMargin + 122, yPos + 5);
          doc.setFont("helvetica", "normal");
          doc.text(
            certificateData.amendmentDate || "",
            leftMargin + 162,
            yPos + 5,
          );

          // Footer on every page
          doc.setFontSize(8);
          doc.setFont("helvetica", "normal");
          const footerText =
            "This is annexure to 'Certificate of Accreditation' and does not require any signature.";
          doc.text(footerText, pageWidth / 2, pageHeight - 10, {
            align: "center",
          });
        },
      });

      if (typeof doc.putTotalPages === 'function') {
        doc.putTotalPages(totalPagesExp);
      }

      // Generate filename with timestamp
      const now = new Date();
      const timestamp =
        now.getFullYear() +
        String(now.getMonth() + 1).padStart(2, "0") +
        String(now.getDate()).padStart(2, "0") +
        "_" +
        String(now.getHours()).padStart(2, "0") +
        String(now.getMinutes()).padStart(2, "0") +
        String(now.getSeconds()).padStart(2, "0");

      const fileName = `SCF_Annexure_${certificateData.certificateNumber}-${timestamp}.pdf`;

      // Return blob for upload and filename
      const pdfBlob = doc.output('blob');
      console.log("✓ SCF Annexure PDF generated successfully");

      return { success: true, message: "SCF Annexure generated successfully", pdfBlob, fileName, doc };
    } else {
      console.warn("No SCF data found");
      return {
        success: false,
        message: "No SCF data found for this application",
      };
    }
  } catch (error) {
    console.error("Error generating SCF Annexure:", error);
    throw new Error(`Failed to generate SCF Annexure: ${error.message}`);
  }
};

export default {
  generateSCFAnnexure,
};
